import {
  UseMutationResult,
  useMutation,
  useQueries,
  useQuery,
} from '@tanstack/react-query'

import {
  getHashtags,
  getMentorByContext,
  getMentorsPooling,
  getQueryClassification,
  getSearchTitles,
  mentorsSearch,
  universalMentorSearch,
  type IMentorByContextResult,
  type MentorsSearchParams,
} from '../../api/search'

export type UseMentorSearchResult = Awaited<ReturnType<typeof mentorsSearch>>

export const useSearchMentors = (
  queryKey: string,
  params: MentorsSearchParams,
  enabled: boolean,
  withPlaceholder = true,
) => {
  return useQuery({
    queryKey: [queryKey, params],
    queryFn: () => mentorsSearch(params),
    enabled,
    placeholderData: prev => {
      if (!enabled || !withPlaceholder) return undefined
      return prev
    },
  })
}

export const useUniversalSearch = ({
  search,
  size,
  isEnabled,
}: MentorsSearchParams & { isEnabled: boolean }) => {
  return useQuery({
    queryKey: ['universal-search', search, size],
    queryFn: () => {
      return universalMentorSearch({ search, size })
    },
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: isEnabled,
  })
}

interface useMentorsByContextParams {
  contextIds: string[]
  scopeId: string
  quantity: number
  isApiEnabled: boolean
}

export const useMentorsByContext = ({
  contextIds,
  scopeId,
  quantity,
  isApiEnabled,
}: useMentorsByContextParams) =>
  useQueries({
    queries: contextIds.map(id => ({
      queryKey: ['get-mentor-by-context', id, scopeId, quantity],
      queryFn: () =>
        getMentorByContext(id, scopeId, quantity).catch(() => undefined),
      enabled: isApiEnabled && !!scopeId,
    })),
    combine: results =>
      results
        .map(r => r.data)
        .filter((d): d is IMentorByContextResult => d !== undefined),
  })

type SearchTitlesResponse = Awaited<ReturnType<typeof getSearchTitles>>

export const useSearchTitles = (): UseMutationResult<
  SearchTitlesResponse,
  unknown,
  string
> => useMutation({ mutationFn: getSearchTitles })

type IMentorsResult = Awaited<ReturnType<typeof getMentorsPooling>>

export const useGetMentorsPooling = (): UseMutationResult<
  IMentorsResult,
  unknown,
  string[]
> => useMutation({ mutationFn: getMentorsPooling })

export const useQueryClassification = (query: string) => {
  return useQuery({
    queryKey: ['query-classification-search', query],
    queryFn: () => {
      return getQueryClassification(query)
    },
    staleTime: 0,
    gcTime: 0,
  })
}

export const useHashtags = () => {
  return useQuery({
    queryKey: ['search-hashtags'],
    queryFn: getHashtags,
  })
}
